import { GroupUpdateDto } from '@bom-nextgen-keycloak/models';
import { selectPermissionGroupDetail } from '@bom-nextgen-keycloak/web/core';
import { FeaturePage } from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  PageLoading,
  ProtectedRouter,
  TextField,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  MESSAGE_NO_BEGINNING_SPACE_REGEX,
  NO_BEGINNING_SPACE_REGEX,
} from '@bom-nextgen-keycloak/web/shared/util';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import { Form, Formik, FormikHelpers } from 'formik';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as yup from 'yup';
import { useGroupDetail } from '../../hooks/useGroupDetail';
import { ActionWrapper, StyledButton } from './Detail.styled';

const validationSchema = yup.lazy((values) => {
  if (values.level === 2) {
    return yup.object({
      name: yup
        .string()
        .required('Group name is required')
        .matches(NO_BEGINNING_SPACE_REGEX, MESSAGE_NO_BEGINNING_SPACE_REGEX)
        .matches(
          /^[aA-zZ0-9-_:() ]+$/,
          'Not allowed special characters for this field'
        ),
    });
  } else {
    return yup.object({
      name: yup
        .string()
        .matches(NO_BEGINNING_SPACE_REGEX, MESSAGE_NO_BEGINNING_SPACE_REGEX)
        .required('Group name is required'),
    });
  }
});

type InitialGroup = {
  id: string;
  name: string;
  level: number;
};

const Detail: FC = () => {
  const params = useParams();
  const groupId = params?.id || '';
  const { groupDetail, isLoading, isLoadingSubmit, handleUpdateGroup } =
    useGroupDetail(groupId);
  const { isEditGeneralTab } = useSelector(selectPermissionGroupDetail);

  const initialValues: InitialGroup = {
    id: groupDetail?.id || '',
    name: groupDetail?.name || '',
    level: groupDetail?.level || 0,
  };

  return (
    <PageLoading isLoading={isLoading}>
      <Formik
        initialValues={initialValues}
        onSubmit={(
          payload: InitialGroup,
          { setSubmitting }: FormikHelpers<InitialGroup>
        ) => {
          const payloadSubmit: GroupUpdateDto = {
            ...payload,
            path: groupDetail.path,
            subGroups: groupDetail.subGroups || [],
            attributes: groupDetail.attributes || {},
            clientRoles: groupDetail.clientRoles || {},
          };
          handleUpdateGroup(payloadSubmit);
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, isValid, dirty, resetForm }) => (
          <Form>
            <Box sx={{ mb: 3 }}>
              <TextField
                name="name"
                label="Group name"
                disabled={groupDetail.level === 3 || !isEditGeneralTab}
                required
              />
            </Box>
            <ActionWrapper>
              <StyledButton
                color="primary"
                variant="contained"
                disabled={!dirty || !isEditGeneralTab}
                onClick={() => resetForm()}
              >
                Reset
              </StyledButton>
              <StyledButton
                type="submit"
                color="primary"
                variant="contained"
                loadingPosition="start"
                startIcon={<SaveIcon />}
                loading={isLoadingSubmit}
                disabled={
                  !(isValid && dirty) || isSubmitting || !isEditGeneralTab
                }
                id="btn-save"
              >
                Save
              </StyledButton>
            </ActionWrapper>
          </Form>
        )}
      </Formik>
    </PageLoading>
  );
};

const WrappedGroupDetailPage: FC = () => {
  const { canViewPage, canViewGeneralTab } = useSelector(
    selectPermissionGroupDetail
  );
  return (
    <ProtectedRouter
      isEditPage
      feature={FeaturePage.GROUP}
      canView={canViewPage && canViewGeneralTab}
      navigatePath="/groups"
    >
      <Detail />
    </ProtectedRouter>
  );
};

export { WrappedGroupDetailPage };
